import classNames from 'classnames';
import DOMPurify from 'dompurify';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import Notifications from 'react-notification-system-redux';
import { connect } from 'react-redux';
import { Element } from 'react-scroll';

import * as AppService from '../../../common/services/app/Service';
import { PMPoliciesState } from '../../../common/services/app/Types';
import * as CustomerService from '../../../common/services/customer/Service';
import { CustomerDataState } from '../../../common/services/customer/Types';
import { CustomerConsentDTO } from '../../../common/types/dto/CustomerConsentDTO';
import { PMPolicyDTO } from '../../../common/types/dto/PMPolicyDTO';
import { TextBlockDTO } from '../../../common/types/dto/TextBlockDTO';
import { UsageDefinitionDTO } from '../../../common/types/dto/UsageDefinitionDTO';
import { Utils } from '../../../common/utils';
import cssCheckbox from '../../assets/scss/Checkbox.scss';
import { RootState } from '../../base/RootReducer';
import { config } from '../../common';
import { useHistory } from '../../common/RouterHooks';
import { AppThunkDispatch } from '../../common/types';
import css from '../../consent/components/PoliciesConsent.scss';

interface ReduxStateProps {
    policies: PMPoliciesState;
    customerData: CustomerDataState;
}

interface ReduxDispatchProps {
    appServiceActions: AppService.ServiceActions;
    customerServiceActions: CustomerService.ServiceActions;
    dispatch: AppThunkDispatch;
}

const PMPolicies: React.FC<ReduxStateProps & ReduxDispatchProps> = (props) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [openInfoDialog, setOpenInfoDialog] = useState(false);
    const [info, setInfo] = useState('');
    const [acceptedUsages, setAcceptedUsages] = useState<number[]>([]);
    const [acceptedTerms, setAcceptedTerms] = useState<number[]>([]);
    const [errorText, setErrorText] = useState('');
    const { t } = useTranslation();
    const history = useHistory();

    const matchAcceptedUsages = (policy: PMPolicyDTO, consents: CustomerConsentDTO[], isTermsAndConditions: boolean) => {
        const newAcceptedUsages = isTermsAndConditions ? [...acceptedTerms] : [...acceptedUsages];
        let hasChanges = false;
        if (policy.textBlocks) {
            policy.textBlocks.forEach((tb) => {
                if (tb.usageDefinitions) {
                    tb.usageDefinitions.forEach((ud) => {
                        if (ud.usages) {
                            ud.usages.forEach((u) => {
                                // get the consents for the specified usage ordered by the consent date descending (consider all touchpoints)
                                const consentsForUsage = consents
                                    .filter((x) => x.usageId === u.name
                                        && x.majorVersion === policy.majorVersion
                                        && x.minorVersion === policy.minorVersion
                                        && x.policyId === policy.name
                                        && x.country === policy.country)
                                    .sort((a, b) => (a.consentDate.toString() > b.consentDate.toString() ? -1 : 1));
                                if (consentsForUsage && consentsForUsage.length > 0) {
                                    // check the latest consent
                                    if (consentsForUsage[0].consent === 'ACCEPT') {
                                        // ensure the accepted usage
                                        const index = newAcceptedUsages.indexOf(u.id);
                                        if (index < 0) {
                                            hasChanges = true;
                                            newAcceptedUsages.push(u.id);
                                        }
                                    }
                                }
                            });
                        }
                    });
                }
            });
        }
        if (hasChanges) {
            if (isTermsAndConditions) {
                setAcceptedTerms(newAcceptedUsages);
            } else {
                setAcceptedUsages(newAcceptedUsages);
            }
        }
    };

    useEffect(() => {
        setIsLoading(true);

        // load policies
        props.appServiceActions.loadPMPolicies().then((policies) => {
            // load user data
            props.customerServiceActions.customerData().then((customerData) => {
                // pre-fill customer consents
                if (policies && customerData.consents) {
                    matchAcceptedUsages(policies.marketing, customerData.consents, false);
                    matchAcceptedUsages(policies.termsAndConditions, customerData.consents, true);
                }
                setIsLoading(false);
            });
        });
    }, []);

    const onOpenInfoDialog = (text: string) => { setOpenInfoDialog(true); setInfo(text); };
    const onCloseInfoDialog = () => { setOpenInfoDialog(false); setInfo(''); };

    const onSelectUsage = (usageId: number) => {
        const newUsagesAccepted = [...acceptedUsages];
        const index = acceptedUsages.indexOf(usageId);
        if (index > -1) {
            newUsagesAccepted.splice(index, 1);
        } else {
            newUsagesAccepted.push(usageId);
        }
        setAcceptedUsages(newUsagesAccepted);
    };

    const onSelectTerm = (usageId: number) => {
        const newTermsAccepted = [...acceptedTerms];
        const index = acceptedTerms.indexOf(usageId);
        if (index > -1) {
            newTermsAccepted.splice(index, 1);
        } else {
            newTermsAccepted.push(usageId);
        }
        setAcceptedTerms(newTermsAccepted);
    };

    const renderUsages = (usageDef: UsageDefinitionDTO, usageDefId: number, textBlockId: number, isTermsAndConditions: boolean) => {
        if (usageDef.usages && usageDef.usages.length > 0) {
            return usageDef.usages.map((usage, key) => {
                const text = (usageDef.usages && usageDef.usages.length === 1) ? usageDef.textPrimary : usage.label;
                return (
                    <div className={[(config.BrandEnv === 'BMW' ? cssCheckbox.bmw : cssCheckbox.mini)].join(' ')} key={key} >
                        <div className={[css.paddingTop10, css.usageDef, cssCheckbox.inputCheckbox].join(' ')}>
                            <label className={[css.label, css.checkbox].join(' ')}>
                                <input
                                    type="checkbox"
                                    checked={isTermsAndConditions === true ? acceptedTerms.indexOf(usage.id) > -1 : acceptedUsages.indexOf(usage.id) > -1}
                                    name={'checkbox-' + usage.id}
                                    data-policy-id={usage.id}
                                    data-policy-mandatory={false}
                                    onChange={() => isTermsAndConditions === true ? onSelectTerm(usage.id) : onSelectUsage(usage.id)}
                                    className={[css.customitemCheckbox, css.floatLeft].join(' ')}
                                />
                                <i />
                                &nbsp; {<div dangerouslySetInnerHTML={{ __html: text ? DOMPurify.sanitize(text, { ADD_ATTR: ['target'] }) : '' }} />}
                            </label>
                        </div>
                    </div>
                );
            });
        }
        return null;
    };

    const renderUsageDef = (textBlock: TextBlockDTO, isTermsAndConditions: boolean) => {
        if (textBlock.usageDefinitions && textBlock.usageDefinitions.length > 0) {
            const usageDefs = textBlock.usageDefinitions.map((usageDef, usagedefkey) => {
                if (usageDef.isImplicit) {
                    // do not render implicit usage definitions (all usages are implicit)
                    return null;
                } else if (usageDef.usages) {
                    // render usages
                    return (
                        <div key={usagedefkey}>
                            {renderUsages(usageDef, usageDef.id, textBlock.id, isTermsAndConditions)}
                        </div>
                    );
                }
                return null;
            });
            return (
                <div className={css.subitems}>
                    <div className={css.rowgdpr}>
                        {usageDefs}
                    </div>
                </div>
            );
        }
        return null;
    };

    const renderHeader = (textBlock: TextBlockDTO) => {
        if (textBlock.usageTexts && textBlock.usageTexts.length > 0) {
            const header = textBlock.usageTexts.find((x) => x.type === config.Constants.UsageTextType.Header);
            if (header && header.value) {
                return (<div className={css.div_cond1_a} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(header.value, { ADD_ATTR: ['target'] }) }} />);
            }
        }
        return null;
    };

    const renderInfo = (textBlock: TextBlockDTO, usageDefType: number) => {
        if (textBlock.usageTexts && textBlock.usageTexts.length > 0) {
            return textBlock.usageTexts.map((usageText, key) => {
                if (usageText.type === usageDefType && usageText.value) {
                    return (
                        <div className={css.paddingTop10} key={key}>
                            <label className={[css.label, css.checkbox].join(' ')} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(usageText.value, { ADD_ATTR: ['target'] }) }} />
                            {usageText.popup &&
                                <a>
                                    <span
                                        onClick={() => { if (usageText.popup) { onOpenInfoDialog(usageText.popup); } }}
                                        className={[css.info, 'info-mkt'].join(' ')}
                                    >
                                        <svg fill="#000000" height="18" viewBox="0 0 24 24" width="18" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M0 0h24v24H0z" fill="none" />
                                            <path d="M11 17h2v-6h-2v6zm1-15C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zM11 9h2V7h-2v2z" />
                                        </svg>
                                    </span>
                                </a>
                            }
                        </div>
                    );
                }
                return null;
            });
        }
        return null;
    };

    const renderPMPolicy = (policy: PMPolicyDTO, isTermsAndConditions: boolean) => {
        if (policy.textBlocks) {
            return policy.textBlocks.map((textBlock, key) => {
                return (
                    <div key={key}>
                        {renderHeader(textBlock)}
                        {renderInfo(textBlock, config.Constants.UsageTextType.BeforeUsageDef)}
                        {renderUsageDef(textBlock, isTermsAndConditions)}
                        {renderInfo(textBlock, config.Constants.UsageTextType.AfterUsageDef)}
                        {renderInfo(textBlock, config.Constants.UsageTextType.Footer)}
                        <Modal
                            isOpen={openInfoDialog}
                            className={css.modalCustom}
                            onAfterOpen={() => Utils.iframeScrollToCenter()}
                        >
                            <Element name="modalElem" id="modalElem" key="modalElem">
                                <div dangerouslySetInnerHTML={{ __html: info ? DOMPurify.sanitize(info, { ADD_ATTR: ['target'] }) : '' }} className={classNames([`${config.BrandEnv}-color-text-modal-signup`, 'margin-bottom-1'])} />
                                <button className={[css.modalCloseBtn, `${config.BrandEnv}-color-button-modal-signup`].join(' ')} onClick={() => onCloseInfoDialog()}>{t('close')}</button>
                            </Element>
                        </Modal>
                    </div>
                );
            });
        }
        return null;
    };

    const handleSubmit = () => {
        if (isLoading) {
            return;
        }

        if (props.policies.data) {
            // check mandatory consents
            setErrorText('');
            let hasError = false;
            if (props.policies.data.termsAndConditions && props.policies.data.termsAndConditions.textBlocks) {
                props.policies.data.termsAndConditions.textBlocks.forEach((tb) => {
                    if (tb.usageDefinitions) {
                        tb.usageDefinitions.forEach((ud) => {
                            if (ud.usages) {
                                ud.usages.forEach((u) => {
                                    if (u.variant === 'MANDATORY' && acceptedTerms.findIndex((x) => x === u.id) < 0) {
                                        hasError = true;
                                        setErrorText(t('policyConsents.mandatory'));
                                    }
                                });
                            }
                        });
                    }
                });
            }

            // submit consents
            if (!hasError) {
                setIsSubmitting(true);
                props.appServiceActions.setPMPolicyConsents({ terms: acceptedTerms, usages: acceptedUsages }).then(
                    () => {
                        setIsSubmitting(false);
                        props.dispatch(Notifications.success({
                            title: t('success'),
                            message: t('policyConsents.success'),
                            position: 'tc',
                            autoDismiss: 5,
                        }));
                        props.customerServiceActions.customerData().then(() => {
                            history.push(config.BaseURL);
                        });
                    },
                    (reason: any) => {
                        setIsSubmitting(false);
                        props.dispatch(Notifications.error({
                            title: t('error'),
                            message: t('common:errorMessages.noPolicyConsents'),
                            position: 'tc',
                            autoDismiss: 5,
                        }));
                    },
                );
            }
        } else {
            setErrorText(t('error'));
        }
    };

    if (props.policies) {
        const displayLoad = isLoading || props.policies.isCommunicating || props.customerData.isCommunicating;
        return (
            <div id="register-checkboxes" className={'col-12'}>
                {displayLoad && <p>{t('loadingText')}</p>}
                {!displayLoad && props.policies.data && renderPMPolicy(props.policies.data.termsAndConditions, true)}
                {!displayLoad && props.policies.data && renderPMPolicy(props.policies.data.marketing, false)}
                {!displayLoad && errorText && errorText !== '' && (<div className="error-message">{errorText}</div>)}
                {!displayLoad && props.policies.data && (
                    <div className={'button-group'}>
                        <button disabled={isSubmitting} className={classNames(['button', 'bttSubscrever'])} onClick={handleSubmit}>{isSubmitting ? t('loadingText') : t('send')}</button>
                    </div>
                )}
            </div>
        );
    }
    return null;
};

const mapStateToProps = (store: RootState) => ({
    policies: store.appState.pmpolicies,
    customerData: store.customerState.customerData,
});

const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
    appServiceActions: AppService.bindServiceActionCreators(config, dispatch),
    customerServiceActions: CustomerService.bindServiceActionCreators(config, dispatch),
    dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(PMPolicies);
